import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Text, mergeStyleSets, Spinner, SpinnerSize } from '@fluentui/react';
import { LockSimple, ShieldCheck, Database, GlobeHemisphereWest, TreeStructure, Table } from '@phosphor-icons/react';
import { datasetService } from '../services/datasetService';
import DepartmentTile from '../components/datasets/DepartmentTile';
import DepartmentDetailView from '../components/datasets/DepartmentDetail';
import { boroondaraPalette } from '../theme/boroondaraTheme';
import type { DepartmentSummary } from '../types/dataset.types';

/* ---------- Filter Types ---------- */
type TierFilter = 'public' | 'governed';

/* ---------- Helpers ---------- */
function fmtCount(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return n.toLocaleString();
}

/* ---------- Styles ---------- */
const styles = mergeStyleSets({
  page: {
    padding: 32,
    minHeight: 'calc(100vh - 52px)',
    background: '#FFFFFF',
    '@media (max-width: 768px)': {
      padding: 16,
    },
  } as any,
  container: {
    maxWidth: 1320,
    margin: '0 auto',
  },
  headerRow: {
    marginBottom: 24,
  },
  title: {
    color: '#232323',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  },
  subtitle: {
    color: '#484848',
    marginTop: 6,
    maxWidth: 720,
    lineHeight: '1.6',
  },
  chipRow: {
    marginTop: 14,
    flexWrap: 'wrap' as const,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 14px',
    borderRadius: 999,
    background: '#F5F5F5',
    border: '1px solid #EDEDED',
    color: '#484848',
    fontSize: 12,
    fontWeight: 500,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 20,
    flexWrap: 'wrap' as const,
  },
  filterGroup: {
    display: 'flex',
    gap: 4,
  },
  filterBtn: {
    padding: '8px 18px',
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 600,
    border: '1px solid #EDEDED',
    background: '#F5F5F5',
    color: '#484848',
    cursor: 'pointer',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap' as const,
    selectors: {
      ':hover': {
        background: '#EBEBEB',
      },
    },
  },
  filterBtnActive: {
    background: '#d9f4d7',
    borderColor: 'rgba(46,135,61,0.4)',
    color: '#2e873d',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
    gap: 16,
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
      gap: 12,
    },
  } as any,
  countText: {
    color: '#747474',
    fontSize: 12,
    marginBottom: 12,
  },
  loading: {
    padding: 64,
    textAlign: 'center' as const,
  },
  error: {
    padding: 24,
    borderRadius: 4,
    background: 'rgba(216,59,1,0.08)',
    border: '1px solid rgba(216,59,1,0.2)',
    color: '#D83B01',
    fontSize: 14,
  },
  emptyState: {
    padding: 48,
    textAlign: 'center' as const,
    color: '#747474',
    fontSize: 14,
  },
  // KPI strip
  kpiRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 16,
    marginBottom: 24,
    '@media (max-width: 768px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: 10,
    },
  } as any,
  kpiCard: {
    padding: 22,
    '@media (max-width: 768px)': { padding: 14 },
    borderRadius: 4,
    background: '#FFFFFF',
    border: '1px solid #E1E1E1',
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
  },
  kpiIconWrap: {
    width: 36,
    height: 36,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  kpiLabel: {
    color: boroondaraPalette.text3,
    fontSize: 11,
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as any,
    fontWeight: 600 as any,
    marginBottom: 8,
  },
  kpiValue: {
    color: boroondaraPalette.text,
    fontWeight: 700 as any,
    fontSize: 28,
    lineHeight: '36px',
    letterSpacing: '-0.02em',
    marginBottom: 4,
    '@media (max-width: 768px)': { fontSize: 22, lineHeight: '28px' },
  } as any,
  kpiHint: { color: boroondaraPalette.text3, fontSize: 12 },
});

/* ---------- Component ---------- */

export default function Datasets() {
  const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tierFilter, setTierFilter] = useState<TierFilter>('public');
  const [selectedDeptId, setSelectedDeptId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link support: ?dept=<id> opens that dataset's detail view directly
  // (used by the source-dataset links in the chat methodology drawer).
  useEffect(() => {
    const dept = searchParams.get('dept');
    if (dept) setSelectedDeptId(dept);
  }, [searchParams]);

  // Clear both the selection and the URL param so a refresh doesn't re-open.
  const clearSelectedDept = useCallback(() => {
    setSelectedDeptId(null);
    if (searchParams.has('dept')) {
      const next = new URLSearchParams(searchParams);
      next.delete('dept');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Load departments
  const loadDepartments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await datasetService.getDatasets();
      setDepartments(data);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load datasets';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  // Filter by tier
  const filtered = useMemo(() => {
    if (tierFilter === 'public') {
      return departments.filter((d) => d.classification === 'OFFICIAL');
    }
    return departments.filter((d) => d.classification !== 'OFFICIAL');
  }, [departments, tierFilter]);

  // Count stats
  const activeCount = departments.filter((d) => d.status === 'active').length;
  const totalTables = departments.reduce((s, d) => s + d.tableCount, 0);
  const totalRows = departments.reduce((s, d) => s + d.totalRows, 0);
  const accessibleCount = departments.filter((d) => d.hasAccess && d.status === 'active').length;

  // If a department is selected, show detail view
  if (selectedDeptId) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <DepartmentDetailView
            departmentId={selectedDeptId}
            onBack={clearSelectedDept}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.headerRow}>
          <Text variant="xxLarge" className={styles.title}>
            Self-Serve Datasets
          </Text>
          <Text variant="medium" className={styles.subtitle}>
            Discover, explore and download governed datasets across council departments.
            Each dataset is classified, owned and access-controlled through Entra ID and role-based permissions.
          </Text>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
            <span className={styles.chip}>
              <LockSimple size={14} /> Entra ID secured
            </span>
            <span className={styles.chip}>
              <ShieldCheck size={14} /> Governed access
            </span>
            <span className={styles.chip}>
              <Database size={14} /> Fabric Warehouse
            </span>
            <span className={styles.chip}>
              <GlobeHemisphereWest size={14} /> Australia East
            </span>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className={styles.loading}>
            <Spinner size={SpinnerSize.large} label="Loading departments..." />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className={styles.error}>
            {error}
            <button
              style={{
                marginLeft: 16,
                padding: '4px 12px',
                borderRadius: 4,
                background: '#F5F5F5',
                border: '1px solid #E1E1E1',
                color: '#232323',
                cursor: 'pointer',
                fontSize: 12,
              }}
              onClick={loadDepartments}
            >
              Retry
            </button>
          </div>
        )}

        {/* Controls & Grid */}
        {!loading && !error && (
          <>
            {/* KPI Strip */}
            <div className={styles.kpiRow}>
              <div className={styles.kpiCard}>
                <div className={styles.kpiIconWrap} style={{ background: 'rgba(46,135,61,0.12)', border: '1px solid #2e873d30' }}>
                  <TreeStructure size={16} color="#2e873d" />
                </div>
                <div className={styles.kpiLabel}>Active Datasets</div>
                <div className={styles.kpiValue}>{activeCount}</div>
                <div className={styles.kpiHint}>of {departments.length} total</div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiIconWrap} style={{ background: 'rgba(124,124,255,0.12)', border: '1px solid #7C7CFF30' }}>
                  <Table size={16} color="#7C7CFF" />
                </div>
                <div className={styles.kpiLabel}>Total Tables</div>
                <div className={styles.kpiValue}>{totalTables}</div>
                <div className={styles.kpiHint}>across all departments</div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiIconWrap} style={{ background: 'rgba(56,189,248,0.12)', border: '1px solid #38BDF830' }}>
                  <Database size={16} color="#38BDF8" />
                </div>
                <div className={styles.kpiLabel}>Total Rows</div>
                <div className={styles.kpiValue}>{fmtCount(totalRows)}</div>
                <div className={styles.kpiHint}>governed data records</div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiIconWrap} style={{ background: 'rgba(52,211,153,0.12)', border: '1px solid #34D39930' }}>
                  <LockSimple size={16} color="#34D399" weight="fill" />
                </div>
                <div className={styles.kpiLabel}>Accessible</div>
                <div className={styles.kpiValue}>{accessibleCount}</div>
                <div className={styles.kpiHint}>departments you can access</div>
              </div>
            </div>

            {/* Tier filter */}
            <div className={styles.controls}>
              <div className={styles.filterGroup}>
                {(['public', 'governed'] as TierFilter[]).map((t) => (
                  <button
                    key={t}
                    className={`${styles.filterBtn} ${tierFilter === t ? styles.filterBtnActive : ''}`}
                    onClick={() => setTierFilter(t)}
                  >
                    {t === 'public' ? 'Public' : 'Governed'}
                  </button>
                ))}
              </div>
            </div>

            {/* Result count */}
            <div className={styles.countText}>
              Showing {filtered.length} {tierFilter === 'public' ? 'public' : 'governed'} datasets
            </div>

            {/* Grid of tiles */}
            {filtered.length > 0 ? (
              <div className={styles.grid}>
                {filtered.map((dept) => (
                  <DepartmentTile
                    key={dept.id}
                    dept={dept}
                    onClick={() => setSelectedDeptId(dept.id)}
                  />
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                No {tierFilter === 'public' ? 'public' : 'governed'} datasets available.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
